const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

export interface HireRatingEventRecord {
  id: string;
  hireeName: string;
  hireeRole: string;
  department: string;
  requisitionSource: string | null;
  hireDate: string;
  managerEmail: string;
  managerName: string;
  roundId: string | null;
  ratedCount: number;
}

export interface CreateHireRatingEventRequest {
  hireeName: string;
  hireeRole: string;
  department: string;
  managerEmail: string;
  managerName: string;
  hireDate: string; // ISO 8601
  requisitionSource?: string;
  roundId?: string;
}

export interface DeliverRoundResult {
  succeeded: number;
  failed: number;
  skipped: number;
  failures: { email: string; reason: string }[];
}

export interface HireRatingImportResult {
  created: number;
  skipped: number;
  errors: string[];
}

async function authFetch(url: string, accessToken: string, init?: RequestInit) {
  const res = await fetch(`${BASE}${url}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res;
}

export async function fetchHireEvents(
  accessToken: string,
  opts?: { roundId?: string; unassigned?: boolean }
): Promise<HireRatingEventRecord[]> {
  const qs = new URLSearchParams();
  if (opts?.roundId) qs.set('roundId', opts.roundId);
  if (opts?.unassigned) qs.set('unassigned', 'true');
  const res = await authFetch(`/api/hire-rating-events?${qs}`, accessToken);
  return res.json();
}

export async function createHireEvent(
  accessToken: string,
  body: CreateHireRatingEventRequest
): Promise<{ id: string }> {
  const res = await authFetch('/api/hire-rating-events', accessToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function deleteHireEvent(
  accessToken: string,
  id: string
): Promise<void> {
  await authFetch(`/api/hire-rating-events/${id}`, accessToken, { method: 'DELETE' });
}

export async function deliverRound(
  accessToken: string,
  roundId: string
): Promise<DeliverRoundResult> {
  const res = await authFetch(`/api/hire-rating-rounds/${roundId}/deliver`, accessToken, {
    method: 'POST',
  });
  return res.json();
}

export function getHireEventsTemplateUrl(): string {
  return `${BASE}/api/hire-rating-events/template`;
}

export async function uploadHireEvents(
  accessToken: string,
  file: File,
  roundId?: string
): Promise<HireRatingImportResult> {
  const form = new FormData();
  form.append('file', file);

  const qs = roundId ? `?roundId=${roundId}` : '';
  const res = await fetch(`${BASE}/api/hire-rating-events/upload${qs}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: form,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { message?: string }).message ?? `Upload failed: ${res.status}`);
  }

  return res.json();
}
